import React, { useState } from 'react';
import '../styles/PhotoUploadGrid.css';
import { FormData } from '../types';

interface Props {
  formData: FormData;
  onChange: (field: keyof FormData, value: string) => void;
}

const PhotoUploadGrid: React.FC<Props> = ({ formData, onChange }) => {
  const [mainPhoto, setMainPhoto] = useState<string | null>(null);
  const [secondPhoto, setSecondPhoto] = useState<string | null>(null);
  const [thirdPhoto, setThirdPhoto] = useState<string | null>(null);

  const handleFileUpload = (
    event: React.ChangeEvent<HTMLInputElement>,
    photoType: 'main' | 'second' | 'third'
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        if (photoType === 'main') setMainPhoto(result);
        else if (photoType === 'second') setSecondPhoto(result);
        else if (photoType === 'third') setThirdPhoto(result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removePhoto = (photoType: 'main' | 'second' | 'third') => {
    if (photoType === 'main') setMainPhoto(null);
    else if (photoType === 'second') setSecondPhoto(null);
    else if (photoType === 'third') setThirdPhoto(null);
  };

  return (
    <div className="photo-upload-grid">
      {/* First Row: Upload, Jewelry Item, Platform & Format */}
      <div className="grid-row">
        <div className="grid-cell upload-cell">
          <h3 className="cell-title">1. Upload Photo</h3>
          <div className="upload-zone">
            <input
              type="file"
              id="main-photo-input"
              accept="image/*"
              onChange={(e) => handleFileUpload(e, 'main')}
              style={{ display: 'none' }}
            />
            <label htmlFor="main-photo-input" className="upload-label">
              {mainPhoto ? (
                <div className="preview-container">
                  <img src={mainPhoto} alt="Main preview" className="preview-image" />
                  <button
                    className="remove-btn"
                    onClick={(e) => {
                      e.preventDefault();
                      removePhoto('main');
                    }}
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <div className="upload-placeholder">
                  <span className="upload-icon">📸</span>
                  <span className="upload-text">Click to upload</span>
                </div>
              )}
            </label>
          </div>
        </div>

        <div className="grid-cell">
          <h3 className="cell-title">2. Select Jewelry Item</h3>
          <select
            className="grid-select"
            value={formData.jewelryItem}
            onChange={(e) => onChange('jewelryItem', e.target.value)}
          >
            <option value="">Choose item...</option>
            <option value="Necklace">Necklace</option>
            <option value="Earring">Earring</option>
            <option value="Ring(s)">Ring(s)</option>
            <option value="Bracelet">Bracelet</option>
            <option value="Pendant">Pendant</option>
            <option value="Anklet">Anklet</option>
            <option value="Brooch">Brooch</option>
            <option value="Charm">Charm</option>
            <option value="Chain">Chain</option>
            <option value="Cuff">Cuff</option>
            <option value="Bangle">Bangle</option>
            <option value="Jewelry Set">Jewelry Set</option>
            <option value="Multiple Pieces (set or layered)">Multiple Pieces</option>
            <option value="Full Collection Display">Full Collection</option>
          </select>
        </div>

        <div className="grid-cell">
          <h3 className="cell-title">3. Platform & Format</h3>
          <select
            className="grid-select"
            value={formData.platform}
            onChange={(e) => onChange('platform', e.target.value)}
          >
            <option value="Instagram">Instagram</option>
            <option value="YouTube">YouTube</option>
            <option value="Facebook">Facebook</option>
            <option value="Etsy">Etsy</option>
            <option value="TikTok">TikTok</option>
            <option value="Pinterest">Pinterest</option>
          </select>
          <select
            className="grid-select"
            value={formData.aspectRatio}
            onChange={(e) => onChange('aspectRatio', e.target.value)}
            style={{ marginTop: '8px' }}
          >
            <option value="1:1 (Square)">1:1 Square</option>
            <option value="4:5 (Portrait)">4:5 Portrait</option>
            <option value="9:16 (Vertical)">9:16 Vertical</option>
            <option value="16:9 (Landscape)">16:9 Landscape</option>
            <option value="3:4 (Portrait)">3:4 Portrait</option>
          </select>
        </div>
      </div>

      {/* Second Row: Three Photo Slots */}
      <div className="grid-row">
        <div className="grid-cell photo-cell">
          <h3 className="cell-title">Main Photo</h3>
          <div className="photo-slot">
            <input
              type="file"
              id="photo-slot-1"
              accept="image/*"
              onChange={(e) => handleFileUpload(e, 'main')}
              style={{ display: 'none' }}
            />
            <label htmlFor="photo-slot-1" className="photo-label">
              {mainPhoto ? (
                <div className="photo-preview">
                  <img src={mainPhoto} alt="Main" className="photo-image" />
                  <button
                    className="remove-photo-btn"
                    onClick={(e) => {
                      e.preventDefault();
                      removePhoto('main');
                    }}
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <div className="photo-placeholder">
                  <span className="photo-icon">🖼️</span>
                  <span className="photo-text">Add Photo</span>
                </div>
              )}
            </label>
          </div>
        </div>

        <div className="grid-cell photo-cell">
          <h3 className="cell-title">Second Photo</h3>
          <div className="photo-slot">
            <input
              type="file"
              id="photo-slot-2"
              accept="image/*"
              onChange={(e) => handleFileUpload(e, 'second')}
              style={{ display: 'none' }}
            />
            <label htmlFor="photo-slot-2" className="photo-label">
              {secondPhoto ? (
                <div className="photo-preview">
                  <img src={secondPhoto} alt="Second" className="photo-image" />
                  <button
                    className="remove-photo-btn"
                    onClick={(e) => {
                      e.preventDefault();
                      removePhoto('second');
                    }}
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <div className="photo-placeholder">
                  <span className="photo-icon">🖼️</span>
                  <span className="photo-text">Add Photo</span>
                </div>
              )}
            </label>
          </div>
        </div>

        <div className="grid-cell photo-cell">
          <h3 className="cell-title">Third Photo</h3>
          <div className="photo-slot">
            <input
              type="file"
              id="photo-slot-3"
              accept="image/*"
              onChange={(e) => handleFileUpload(e, 'third')}
              style={{ display: 'none' }}
            />
            <label htmlFor="photo-slot-3" className="photo-label">
              {thirdPhoto ? (
                <div className="photo-preview">
                  <img src={thirdPhoto} alt="Third" className="photo-image" />
                  <button
                    className="remove-photo-btn"
                    onClick={(e) => {
                      e.preventDefault();
                      removePhoto('third');
                    }}
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <div className="photo-placeholder">
                  <span className="photo-icon">🖼️</span>
                  <span className="photo-text">Add Photo</span>
                </div>
              )}
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhotoUploadGrid;






